import styled from 'styled-components';
import { Link } from 'ui/links';
import { backgroundColors, colors, fontWeights, overline, spacing } from 'ui/theme';
import { Tooltip } from 'ui/tooltip';
import { localize } from 'utils/numberHelpers';

export type TableColumn = {
  /** Align text to the right if column is numeric */
  isNumeric?: boolean;
  /** Value to display for cell */
  label: string;
};

export type MetricName = {
  /** Value to display for cell */
  label: string;
  /** Additional text to display as in a Tooltip */
  description?: string;
  /** Link for text */
  to?: string;
};

export type MetricCell = {
  /** Total values may display differently as in bold */
  isTotal?: boolean;
  value: number;
};

export type TableMetric = {
  metricName: MetricName;
  values: MetricCell[];
};

export type AnalyticTableProps = {
  columns: TableColumn[];
  gridLayout: string;
  metrics: TableMetric[];
};

/** Styles */
const Table = styled.div`
  & + & {
    margin-top: ${spacing.double};
  }
`;

const TableRow = styled.div`
  display: grid;
  grid-template-columns: ${({ gridLayout }: { gridLayout: string }) => gridLayout};
  padding: ${spacing.half} ${spacing.base};

  &:nth-child(even) {
    background-color: ${backgroundColors.backgroundSecondary};
  }
`;

const HeaderRow = styled(TableRow)`
  padding-bottom: ${spacing.base};
`;

const Cell = styled.div`
  &[data-numeric="true"] {
    text-align: right;
    color: ${colors.textSecondary};
  }

  &[data-total="true"] {
    font-weight: ${fontWeights.bold};
    color: ${colors.textPrimary};
  }
`;

const HeaderCell = styled(Cell)`
  ${overline}
  color: ${colors.textSecondary};
`;

/** Components */
export const AnalyticTable = ({ columns, gridLayout, metrics = [] }: AnalyticTableProps) => {
  return metrics.length > 0 ? (
    <Table>
      <HeaderRow data-test="analytic-table-header" gridLayout={gridLayout}>
        {columns.map((column) => (
          <HeaderCell data-numeric={column?.isNumeric} key={column.label}>
            {column.label}
          </HeaderCell>
        ))}
      </HeaderRow>
      {metrics.map((metric) => {
        return (
          <TableRow
            data-test="analytic-table-row"
            gridLayout={gridLayout}
            key={metric.metricName.label}
          >
            <Cell>
              {metric.metricName.to ? (
                <Link href={metric.metricName.to} useReactRouter>
                  {metric.metricName.label}
                </Link>
              ) : (
                <Tooltip
                  isEnabled={!!metric.metricName.description}
                  isUnderlined
                  tooltipContent={metric.metricName.description}
                >
                  {metric.metricName.label}
                </Tooltip>
              )}
            </Cell>
            {metric.values.map((value, index) => (
              <Cell key={index} data-numeric="true" data-total={!!value?.isTotal}>
                {localize(value.value)}
              </Cell>
            ))}
          </TableRow>
        );
      })}
    </Table>
  ) : (
    <></>
  );
};
